import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config';
import { handlePhotoButtonClick } from '../utils/photoHandler';
import { handleSocioUpdate } from '../utils/socioUpdateHandler';
import { construirUrlFoto } from '../utils/photoUrlHelper';
import './PanelSocio.css';

function PanelSocio() {
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem('usuario'));
  const [form, setForm] = useState({
    nombreCompleto: usuario.nombreCompleto || '',
    direccion: usuario.direccion || '',
    correo: usuario.correo || '',
    telefono: usuario.telefono || '',
    fechaNacimiento: usuario.fechaNacimiento ? usuario.fechaNacimiento.substring(0, 10) : '',
    novedadesMail: usuario.novedadesMail !== false,
    // foto: usuario.foto || '' // Se manejará por separado
  });
  const [codigoPais, setCodigoPais] = useState('+598'); // Uruguay por defecto
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(
    usuario.foto ? construirUrlFoto(usuario.foto) : ''
  );
  const [fotoAnterior, setFotoAnterior] = useState(usuario.foto);
  const [fotoKey, setFotoKey] = useState(Date.now());
  const [nuevaFoto, setNuevaFoto] = useState(null);
  const [cambioPass, setCambioPass] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nuevaPass, setNuevaPass] = useState('');
  const [passStep, setPassStep] = useState(1); // 1: solicitar código, 2: ingresar código y nueva pass
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  // Función para formatear fecha de manera amigable
  const formatearFecha = (fecha) => {
    if (!fecha) return 'No disponible';
    
    try {
      const fechaObj = new Date(fecha);
      if (isNaN(fechaObj.getTime())) return 'Fecha inválida';
      
      return fechaObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return fecha; // Si falla el formateo, mostrar la fecha original
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCodigoPaisChange = e => {
    setCodigoPais(e.target.value);
  };

  const handleTelefonoChange = e => {
    setForm({ ...form, telefono: e.target.value });
  };

  const validar = () => {
    if (!form.nombreCompleto.trim()) return 'El nombre completo es obligatorio.';
    if (form.correo && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.correo)) return 'El correo no es válido.';
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    await handleSocioUpdate({
      form,
      socio: usuario,
      codigoPais,
      fotoAnterior,
      nuevaFoto,
      setMensaje,
      setError,
      setLoading,
      setSocio: (updatedSocio) => {
        // Actualizar localStorage con los datos actualizados
        localStorage.setItem('usuario', JSON.stringify(updatedSocio));
        // Limpiar nuevaFoto después de guardar exitosamente
        setNuevaFoto(null);
      },
      setForm,
      setFotoPreview,
      setFotoAnterior,
      setFotoKey,
      socioId: usuario.id,
      validar
    });
  };

  const handleFotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFoto(file);
      setNuevaFoto(file);
      setFotoPreview(URL.createObjectURL(file));
      
      // Resetear el input para permitir seleccionar el mismo archivo de nuevo
      e.target.value = '';
    }
  };


  const solicitarCodigo = async () => {
    setMensaje(''); setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SOLICITAR_CAMBIO_CONTRASENA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documento: usuario.documento })
      });
      const data = await res.json();
      if (res.ok) {
        setMensaje('Código enviado a tu correo.');
        setPassStep(2);
      } else {
        setError(data.message || 'Error al solicitar código');
      }
    } catch (err) {
      setError('Error de red');
    }
  };

  const confirmarCambioPass = async (e) => {
    e.preventDefault();
    setMensaje(''); setError('');
    try {
      const res = await fetch(API_ENDPOINTS.CONFIRMAR_CAMBIO_CONTRASENA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documento: usuario.documento, codigo, nuevaContrasena: nuevaPass })
      });
      const data = await res.json();
      if (res.ok) {
        setMensaje('Contraseña actualizada correctamente');
        setCambioPass(false); setPassStep(1); setCodigo(''); setNuevaPass('');
      } else {
        setError(data.message || 'Error al cambiar contraseña');
      }
    } catch (err) {
      setError('Error de red');
    }
  };

  // Función para abrir un PDF protegido
  const abrirPDF = async (nombreArchivo) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No hay sesión iniciada. Por favor, inicia sesión nuevamente.');
        navigate('/login');
        return;
      }

      const url = `${API_ENDPOINTS.DOCUMENTOS}/${nombreArchivo}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401 || response.status === 403) {
        setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        navigate('/login');
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || 'Error al cargar el documento');
        return;
      }

      // Obtener el blob del PDF
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Abrir el PDF en una nueva ventana/pestaña
      const newWindow = window.open(blobUrl, '_blank');
      
      // Limpiar el URL del blob después de que se abra la ventana
      // Usamos un tiempo mayor para asegurar que el navegador cargue el blob
      if (newWindow) {
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } else {
        // Si el navegador bloqueó la ventana emergente, revocar inmediatamente
        window.URL.revokeObjectURL(blobUrl);
        setError('Por favor, permite ventanas emergentes para ver los documentos.');
      }
    } catch (err) {
      console.error('Error al abrir PDF:', err);
      setError('Error al abrir el documento. Por favor, intenta nuevamente.');
    }
  };

  return (
    <div className="panel-socio-bg">
      <div className="panel-socio-card">
        <h2 className="panel-socio-title">👤 Panel del Socio</h2>
        
        {/* Sección de foto */}
        <div style={{marginBottom: 24, width: '100%', textAlign: 'center'}}>
          <h4 style={{marginBottom: 16, color: '#D32F2F', fontSize: '1.1rem'}}>📸 Foto de Perfil</h4>
          
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16}}>
            {fotoPreview && (
              <img 
                src={fotoPreview} 
                alt="Foto del socio" 
                className="panel-socio-foto"
                style={{marginRight: 16}}
              />
            )}
            <button
              type="button"
              className="btn-panel-socio btn-cambiar-foto"
              onClick={() => fileInputRef.current?.click()}
            >
              📷 Cambiar foto
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{display: 'none'}}
              onChange={handleFotoChange}
            />
          </div>
        </div>
        
        {/* Información del socio */}
        <div className="panel-socio-datos">
          <div><strong>Número de Socio:</strong> <span>{usuario.numeroSocio}</span></div>
          <div><strong>Documento:</strong> <span>{usuario.documento}</span></div>
          <div><strong>Tipo Documento:</strong> <span>{usuario.tipoDocumento}</span></div>
          <div><strong>Fecha de Ingreso:</strong> <span>{formatearFecha(usuario.fechaIngreso)}</span></div>
          <div><strong>Categoría:</strong> <span>{usuario.categoria}</span></div>
          <div><strong>Año última cuota paga:</strong> <span>{usuario.anioUltimaCuota}</span></div>
        </div>

        {/* Sección de documentos PDF */}
        <div style={{
          width: '100%',
          marginTop: '24px',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <h4 style={{marginBottom: '16px', color: '#D32F2F', fontSize: '1.1rem', textAlign: 'center'}}>
            📄 Documentos
          </h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <button
              type="button"
              className="btn-panel-socio"
              onClick={() => abrirPDF('EstatutoMiramarMisiones.pdf')}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#D32F2F',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'background-color 0.3s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#B71C1C'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#D32F2F'}
            >
              📄 Ver Estatuto Vigente 1980
            </button>
            <button
              type="button"
              className="btn-panel-socio"
              onClick={() => abrirPDF('EstatutoAprobadoMiramarMisiones.pdf')}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#D32F2F',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'background-color 0.3s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#B71C1C'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#D32F2F'}
            >
              📄 Ver Estatuto Aprobado
            </button>
          </div>
        </div>

        {/* Formulario de edición */}
        <form onSubmit={handleSubmit} className="panel-socio-form">
          <div className="form-columns">
            <div className="form-column">
              <label>Nombre Completo</label>
              <input name="nombreCompleto" value={form.nombreCompleto} onChange={handleChange} required />
            </div>
            <div className="form-column">
              <label>Dirección</label>
              <input name="direccion" value={form.direccion} onChange={handleChange} />
            </div>
          </div>
          
          <div className="form-columns">
            <div className="form-column">
              <label>Correo electrónico</label>
              <input name="correo" type="email" value={form.correo} onChange={handleChange} />
            </div>
            <div className="form-column">
              <label>Teléfono</label>
              <input name="telefono" value={form.telefono} onChange={handleTelefonoChange} />
            </div>
          </div>
          
          <div className="form-bottom-row">
            <label>Fecha de nacimiento</label>
            <input name="fechaNacimiento" type="date" value={form.fechaNacimiento} onChange={handleChange} />
          </div>
          
          <div style={{
            marginTop: '16px',
            fontSize: '0.85rem',
            color: '#666',
            textAlign: 'center',
            fontStyle: 'italic',
            lineHeight: '1.4'
          }}>
            Por sugerencias, reclamos o datos erróneos escribir a <a href="mailto:mail@miramarmisiones.org.uy" style={{color: '#D32F2F', textDecoration: 'none'}}>mail@miramarmisiones.org.uy</a>
          </div>
          
          {mensaje && <div className="panel-socio-mensaje">{mensaje}</div>}
          {error && <div className="panel-socio-error">{error}</div>}
        </form>

        {/* Checkbox de novedades por mail - fuera del form */}
        <div style={{width: '100%', display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem'}}>
          <label style={{
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}>
            <input 
              type="checkbox" 
              name="novedadesMail" 
              checked={form.novedadesMail || false} 
              onChange={e => setForm({ ...form, novedadesMail: e.target.checked })} 
              style={{
                width: '16px',
                height: '16px',
                margin: 0, 
                cursor: 'pointer',
                accentColor: '#D32F2F',
                flexShrink: 0
              }}
            />
            <span>Deseo recibir novedades por mail</span>
          </label>
        </div>

        {/* Botones de acción */}
        <div className="panel-socio-botones">
          <button type="button" className="btn-panel-socio btn-accion" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Guardando...' : '💾 Guardar cambios'}
          </button>
        </div>

        {/* Sección de cambio de contraseña */}
        <button 
          type="button" 
          className="btn-panel-socio btn-accion" 
          onClick={() => setCambioPass(!cambioPass)}
          style={{marginTop: 16, marginBottom: 8}}
        >
          {cambioPass ? '❌ Cancelar cambio de contraseña' : '🔐 Cambiar contraseña'}
        </button>

        {cambioPass && (
          <div style={{marginTop: 16, width: '100%'}}>
            {passStep === 1 ? (
              <div style={{textAlign: 'center', marginBottom: 16}}>
                <p style={{marginBottom: 16, color: '#666'}}>
                  Se enviará un código de verificación a tu correo electrónico.
                </p>
                <button 
                  type="button" 
                  className="btn-panel-socio btn-accion"
                  onClick={solicitarCodigo}
                >
                  📧 Solicitar código
                </button>
              </div>
            ) : (
              <form onSubmit={confirmarCambioPass} className="panel-socio-form">
                <div className="form-bottom-row">
                  <label>Código recibido por mail</label>
                  <input 
                    value={codigo} 
                    onChange={e => setCodigo(e.target.value)} 
                    required 
                    placeholder="Ingresa el código"
                  />
                </div>
                <div className="form-bottom-row">
                  <label>Nueva contraseña</label>
                  <input 
                    type="password" 
                    value={nuevaPass} 
                    onChange={e => setNuevaPass(e.target.value)} 
                    required 
                    placeholder="Nueva contraseña"
                  />
                </div>
                <button type="submit" className="btn-panel-socio btn-accion">
                  ✅ Confirmar cambio
                </button>
              </form>
            )}
          </div>
        )}

        {/* Botón de cerrar sesión */}
        <button 
          className="btn-panel-socio btn-cerrar-sesion" 
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('usuario');
            navigate('/login');
          }}
          style={{marginTop: 16}}
        >
          🚪 Cerrar sesión
        </button>
        <div style={{
          marginTop: '24px',
          fontSize: '0.75rem',
          color: '#666',
          textAlign: 'right',
          fontStyle: 'italic'
        }}>
          Licenciado por Ing. Javier Barrios para uso exclusivo de Club Sportivo Miramar Misiones
        </div>
      </div>
    </div>
  );
}

export default PanelSocio; 